import {
  Box,
  FieldText,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import type { WidgetFlowT } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import {
  Field,
  Form,
  FormErrors,
  InjectedFormProps,
  reduxForm,
  SubmissionError,
} from 'redux-form';

import type { FlowChatActionT } from 'components/powerChatFlow/api/types';
import { withFlows, WithFlowsInjectedPropsT, withWidgets } from 'hocs';
import type { WithWidgetsInjectedPropsT } from 'hocs';
import { useNotifications } from 'hooks/useNotifications';
import { validateName } from 'utils/formValidator';

type RenameWidgetModalOwnPropsT = {
  isOpen: boolean;
  onClose: () => void;
  refreshPage: () => void;
  widget: WidgetFlowT;
};

type RenameWidgetFormValuesT = {
  name: string;
};

type PropsT = RenameWidgetModalOwnPropsT &
  WithWidgetsInjectedPropsT &
  WithFlowsInjectedPropsT<FlowChatActionT> &
  InjectedFormProps<
    RenameWidgetFormValuesT,
    RenameWidgetModalOwnPropsT,
    FormErrors<RenameWidgetFormValuesT>
  >;

const FETCH_DELAY = 1600;
const MAX_LENGTH = 255;

const RenameWidgetModalImpl = (props: PropsT): JSX.Element => {
  const notify = useNotifications();

  const handleFormSubmit = async (
    values: RenameWidgetFormValuesT,
  ): Promise<void> => {
    ampli.chatFormSubmitted({ mode: 'update' });

    const { flows, onClose, refreshPage, widget, widgets } = props;

    try {
      const { name } = values;
      const widgetFlowId = widget.flows[0].id;

      const [{ data: flow }] = await Promise.all([
        flows.getItem(widgetFlowId),
        widgets.update(widget.id, {
          updates: {
            name,
          },
        }),
      ]);
      await flows.update(widgetFlowId, {
        ...flow,
        name,
      });

      notify({
        title: 'Widget updated',
        text: `"${name}" widget has been updated`,
        icon: 'done',
        className: 'success',
        time: 5000,
      });

      onClose();
      // fetch widget items with delay to make sure
      // the new list is updated
      setTimeout(() => {
        refreshPage();
      }, FETCH_DELAY);
    } catch {
      notify({
        title: 'An error occurred',
        text: `An error occurred when trying to update the "${widget.name}" widget`,
        icon: 'exclamation',
        className: 'danger',
      });

      throw new SubmissionError({
        name: 'Widget could not be updated',
      });
    }
  };

  const handleCancel = () => {
    ampli.chatFormCanceled({ mode: 'update' });
    props.onClose();
  };

  const { handleSubmit, isOpen, pristine } = props;

  return (
    <Modal isOpen={isOpen} onClose={handleCancel}>
      <Box width="600px">
        <ModalHead onClose={handleCancel} title="Update widget" />
        <Form onSubmit={handleSubmit(handleFormSubmit)}>
          <ModalBody>
            <Field
              label="Name"
              name="name"
              maxLength={MAX_LENGTH}
              component={FieldText}
              type="text"
              placeholder="Widget name"
              required
              validate={validateName}
            />
          </ModalBody>
          <ModalFoot
            cancelOnClick={handleCancel}
            confirmName="Update"
            confirmOnClick={handleSubmit(handleFormSubmit)}
            confirmProps={{
              disabled: pristine,
              type: 'submit',
            }}
          />
        </Form>
      </Box>
    </Modal>
  );
};

export const RenameWidgetModal = reduxForm<
  RenameWidgetFormValuesT,
  RenameWidgetModalOwnPropsT,
  FormErrors
>({
  enableReinitialize: true,
  form: 'renameWidgetForm',
  touchOnBlur: false,
})(withWidgets(withFlows<FlowChatActionT, PropsT>(RenameWidgetModalImpl)));
